import React, { useRef, useState } from "react";
import { ArrowRight, Check, LoaderCircle, TriangleAlert } from "lucide-react";

import { useT } from "../../i18n";
import { useTemplateVars } from "../../i18n/vars";
import { company, contact } from "../../config";

// The form has to actually work: somebody verifying this company may submit it.
//
// Two modes, chosen by whether REACT_APP_CONTACT_ENDPOINT is set:
//   endpoint set   -> POST JSON, inline success or failure
//   endpoint empty -> prefilled mailto, which is honest about what is happening
//                     rather than pretending to send
//
// It never silently swallows a submission. All five states (idle, focus,
// field error, sending, sent, failed) are specified in the design file under
// "Spec / Contact Form States".

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function Field({ id, label, error, children }) {
  return (
    <div className="flex flex-col gap-2.5">
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      {children}
      {error && (
        <p id={`${id}-error`} className="error-text animate-fade-in">
          {error}
        </p>
      )}
    </div>
  );
}

export default function ContactForm() {
  const { t } = useT();
  const vars = useTemplateVars();

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  // Two cheap anti-spam signals, both checked server side. A honeypot field
  // that only an automated filler will touch, and how long the form was open
  // before submit. Neither costs a real person anything, unlike a captcha.
  const honeypot = useRef(null);
  const mountedAt = useRef(Date.now());

  const set = (field) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: null } : prev));
  };

  function validate() {
    const next = {};
    if (!form.name.trim()) next.name = t("contact.formRequired");
    if (!form.email.trim()) next.email = t("contact.formRequired");
    else if (!EMAIL_RE.test(form.email.trim())) next.email = t("contact.formInvalidEmail");
    if (!form.message.trim()) next.message = t("contact.formRequired");
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function mailtoFallback() {
    if (!company.email) {
      setStatus("error");
      return;
    }
    const subject = encodeURIComponent(`Enquiry from ${form.name || "the website"}`);
    const body = encodeURIComponent(`${form.message}\n\n${form.name}\n${form.email}`);
    window.location.href = `mailto:${company.email}?subject=${subject}&body=${body}`;
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    if (!contact.endpoint) {
      mailtoFallback();
      return;
    }

    setStatus("sending");
    try {
      const res = await fetch(contact.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          company: honeypot.current ? honeypot.current.value : "",
          elapsed: Date.now() - mountedAt.current,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus("sent");
      setForm({ name: "", email: "", message: "" });
    } catch {
      setStatus("error");
    }
  }

  if (status === "sent") {
    return (
      <div className="panel p-8 sm:p-10">
        <div className="callout animate-fade-in flex gap-3 border-accent bg-chip-accent">
          <Check size={17} strokeWidth={2.2} className="mt-0.5 shrink-0 text-accent" aria-hidden="true" />
          <p className="font-body text-[14.5px] leading-[1.55] text-fg" role="status">
            {t("contact.formSuccess")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="panel flex flex-col gap-[26px] p-6 sm:p-10">
      {/* Honeypot. Hidden from people and from screen readers, visible to bots
          that fill every input they can find. */}
      <input
        ref={honeypot}
        type="text"
        name="company"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="pointer-events-none absolute h-0 w-0 opacity-0"
      />

      <Field id="name" label={t("contact.formName")} error={errors.name}>
        <input
          id="name"
          name="name"
          className={`field-input ${errors.name ? "field-error" : ""}`}
          placeholder={t("contact.formNamePlaceholder")}
          value={form.name}
          onChange={set("name")}
          aria-invalid={!!errors.name}
          aria-describedby={errors.name ? "name-error" : undefined}
        />
      </Field>

      <Field id="email" label={t("contact.formEmail")} error={errors.email}>
        <input
          id="email"
          name="email"
          type="email"
          className={`field-input ${errors.email ? "field-error" : ""}`}
          placeholder={t("contact.formEmailPlaceholder")}
          value={form.email}
          onChange={set("email")}
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? "email-error" : undefined}
        />
      </Field>

      <Field id="message" label={t("contact.formMessage")} error={errors.message}>
        <textarea
          id="message"
          name="message"
          className={`field-textarea ${errors.message ? "field-error" : ""}`}
          placeholder={t("contact.formMessagePlaceholder")}
          value={form.message}
          onChange={set("message")}
          aria-invalid={!!errors.message}
          aria-describedby={errors.message ? "message-error" : undefined}
        />
      </Field>

      <div className="flex flex-col gap-4">
        <button
          type="submit"
          className="btn btn-accent w-fit px-[30px] py-[15px] text-[16px]"
          disabled={status === "sending"}
        >
          {status === "sending" ? (
            <>
              <LoaderCircle size={16} strokeWidth={2} className="animate-spin-slow" aria-hidden="true" />
              <span>{t("contact.formSending")}</span>
            </>
          ) : (
            <>
              <span>{t("contact.formSubmit")}</span>
              <ArrowRight size={16} strokeWidth={2} className="btn-arrow" aria-hidden="true" />
            </>
          )}
        </button>

        {status === "error" && (
          <div className="callout animate-fade-in flex gap-3 border-accent-2 bg-chip-warn" role="alert">
            <TriangleAlert
              size={17}
              strokeWidth={2}
              className="mt-0.5 shrink-0 text-accent-2"
              aria-hidden="true"
            />
            <p className="font-body text-[14.5px] leading-[1.55] text-fg">
              {company.email ? t("contact.formError", vars) : t("contact.formErrorNoEmail")}
            </p>
          </div>
        )}
      </div>
    </form>
  );
}
